/* eslint-disable @typescript-eslint/consistent-type-definitions */
import { useEffect, useReducer, useState } from "react";
import clsx from "clsx";
import {
  Columns3Icon,
  TableIcon,
  RotateCcwIcon,
  SearchIcon,
  XIcon,
} from "lucide-react";

import {
  JobPipelineKanban,
  JobPipelineLocationFilter,
  JobPipelineNumberOfCandidatesAndPublished,
  JobPipelineSortBy,
  JobPipelineStatusFilter,
  JobPipelineTable,
} from "@/components";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import { defaultCandidaciesSortKeyAndOrder } from "@/lib/constants";
import { locations } from "@/lib/locations";
import {
  CandidaciesSortKeyAndOrder,
  CandidacyStatus,
  CandidacyWithProfileResponse,
  Job,
} from "@/types";

enum Tab {
  Table,
  Kanban,
}

const Switcher = ({
  disabled,
  setTab,
  tab,
}: {
  disabled?: boolean;
  setTab: (tab: Tab) => void;
  tab: Tab;
}) => {
  return (
    <div>
      <Button
        // TODO: See if there is a better way to deal with the width of the outline variant.
        className={clsx("rounded-l-full", tab !== Tab.Table && "w-[48px]")}
        disabled={disabled}
        onClick={() => {
          setTab(Tab.Table);
        }}
        variant={tab === Tab.Table ? "default" : "outline"}
      >
        <TableIcon className="h-4 w-4" />
      </Button>
      <Button
        // TODO: See if there is a better way to deal with the width of the outline variant.
        className={clsx("rounded-r-full", tab !== Tab.Kanban && "w-[48px]")}
        disabled={disabled}
        onClick={() => {
          setTab(Tab.Kanban);
        }}
        variant={tab === Tab.Kanban ? "default" : "outline"}
      >
        <Columns3Icon className="h-4 w-4" />
      </Button>
    </div>
  );
};

type FilterAndSortState = {
  name: string;
  sortKeyAndOrder: CandidaciesSortKeyAndOrder;
  locations: string[];
  statuses: CandidacyStatus[];
};

type State = {
  allCandidacies: CandidacyWithProfileResponse[];
  filteredAndSortedCandidacies: CandidacyWithProfileResponse[];
  filterAndSortState: FilterAndSortState;
};

type Action =
  | { type: "name"; payload: string }
  | { type: "sortOrderAndKey"; payload: CandidaciesSortKeyAndOrder }
  | { type: "locations"; payload: string[] }
  | { type: "statuses"; payload: CandidacyStatus[] }
  | { type: "candidacies"; payload: CandidacyWithProfileResponse[] };

const filterAndSortCandidacies = ({
  filterAndSortState,
  candidacies,
}: {
  filterAndSortState: FilterAndSortState;
  candidacies: CandidacyWithProfileResponse[];
}) => {
  let filteredAndSortedCandidacies = [...candidacies];

  // 1. Filter by location.
  const locationSet = new Set(filterAndSortState.locations);
  filteredAndSortedCandidacies = filteredAndSortedCandidacies.filter(
    ({ candidateProfile }) => {
      // If the location set is empty, then we are in a default
      // state and we want to show candidacies across all locations.
      if (locationSet.size === 0) {
        return true;
      }

      // If a candidate doesn't have a location just include them.
      if (!candidateProfile.profile.location) {
        return true;
      }
      return locationSet.has(
        locations.stripCountrySuffix(candidateProfile.profile.location),
      );
    },
  );

  // 2. Filter by candidacy status.
  const statusesSet = new Set(filterAndSortState.statuses);
  filteredAndSortedCandidacies = filteredAndSortedCandidacies.filter(
    ({ candidacy }) =>
      // If the statuses set is empty, then we are in a default
      // state and we only want show candidacies that are not rejected.
      (statusesSet.size === 0 &&
        candidacy.status !== CandidacyStatus.REJECTED) ||
      statusesSet.has(candidacy.status),
  );

  // 3. Filter by candidate name.
  filteredAndSortedCandidacies = filteredAndSortedCandidacies.filter(
    ({ candidateProfile }) => {
      // If a candidate doesn't have a first or last name just include them.
      if (
        !candidateProfile.profile.firstName ||
        !candidateProfile.profile.lastName
      ) {
        return true;
      }

      return `${candidateProfile.profile.firstName} ${candidateProfile.profile.lastName}`
        .toLowerCase()
        .includes(filterAndSortState.name.toLowerCase());
    },
  );

  // 4. Sort the candidacies.
  filteredAndSortedCandidacies.sort((candidacyA, candidacyB) => {
    switch (filterAndSortState.sortKeyAndOrder) {
      case CandidaciesSortKeyAndOrder.TimeInStageAsc:
        return (
          new Date(candidacyB.timeInStage).getTime() -
          new Date(candidacyA.timeInStage).getTime()
        );
      case CandidaciesSortKeyAndOrder.TimeInStageDesc:
        return (
          new Date(candidacyA.timeInStage).getTime() -
          new Date(candidacyB.timeInStage).getTime()
        );
      case CandidaciesSortKeyAndOrder.ScoreDesc:
        if (candidacyB.candidacyScore === null) {
          return -1;
        } else if (candidacyA.candidacyScore === null) {
          return 1;
        }
        return candidacyB.candidacyScore - candidacyA.candidacyScore;
      default:
        return 0;
    }
  });

  return filteredAndSortedCandidacies;
};

const reducer = (state: State, action: Action): State => {
  if (action.type === "candidacies") {
    // If new candidacies came in, then re-filter and re-sort based on the new candidacies.
    return {
      ...state,
      allCandidacies: action.payload,
      filteredAndSortedCandidacies: filterAndSortCandidacies({
        filterAndSortState: state.filterAndSortState,
        candidacies: action.payload,
      }),
    };
  }

  // Otherwise, we are updating the filter and sort state.
  let newFilterAndSortState: FilterAndSortState = {
    ...state.filterAndSortState,
  };
  switch (action.type) {
    case "name":
      newFilterAndSortState = {
        ...state.filterAndSortState,
        name: action.payload,
      };
      break;
    case "sortOrderAndKey":
      newFilterAndSortState = {
        ...state.filterAndSortState,
        sortKeyAndOrder: action.payload,
      };
      break;
    case "locations":
      newFilterAndSortState = {
        ...state.filterAndSortState,
        locations: action.payload,
      };
      break;
    case "statuses":
      newFilterAndSortState = {
        ...state.filterAndSortState,
        statuses: action.payload,
      };
      break;
    default:
      break;
  }

  // Set the new filter and sort state as well and filter the candidacies based on it.
  return {
    ...state,
    filteredAndSortedCandidacies: filterAndSortCandidacies({
      filterAndSortState: newFilterAndSortState,
      candidacies: state.allCandidacies,
    }),
    filterAndSortState: newFilterAndSortState,
  };
};

export const JobPipelineTableOrKanban = ({
  candidaciesWithProfiles,
  className,
  job,
}: {
  candidaciesWithProfiles: CandidacyWithProfileResponse[];
  className?: string;
  job: Job;
}) => {
  const [tab, setTab] = useState<Tab>(Tab.Table);

  const allLocations = Array.from(
    new Set(
      candidaciesWithProfiles
        .map((c) => c.candidateProfile.profile.location)
        .filter((location) => location !== null)
        .map(locations.stripCountrySuffix),
    ),
  );
  const [state, dispatch] = useReducer(reducer, {
    allCandidacies: candidaciesWithProfiles,
    filteredAndSortedCandidacies: candidaciesWithProfiles,
    filterAndSortState: {
      sortKeyAndOrder: defaultCandidaciesSortKeyAndOrder,
      name: "",
      locations: [],
      statuses: [],
    },
  });

  useEffect(() => {
    // ! TODO: Need to make sure that all locations wouldn't change...
    // If new candidacies come in then update the state.
    dispatch({ type: "candidacies", payload: candidaciesWithProfiles });
  }, [candidaciesWithProfiles]);

  const setLocations = (locations: string[]) => {
    dispatch({ type: "locations", payload: locations });
  };

  const setSortOrderAndKey = (sortOrderAndKey: CandidaciesSortKeyAndOrder) => {
    dispatch({ type: "sortOrderAndKey", payload: sortOrderAndKey });
  };

  const setStatuses = (statuses: CandidacyStatus[]) => {
    dispatch({ type: "statuses", payload: statuses });
  };

  const setName = (name: string) => {
    dispatch({ type: "name", payload: name });
  };

  const isSearchInDefaultState = state.filterAndSortState.name === "";
  const isSortInDefaultState =
    state.filterAndSortState.sortKeyAndOrder ===
    defaultCandidaciesSortKeyAndOrder;
  const isLocationFilterInDefaultState =
    state.filterAndSortState.locations.length === 0;
  const isStatusFilterInDefaultState =
    state.filterAndSortState.statuses.length === 0;

  const shouldShowResetButton =
    !isSearchInDefaultState ||
    !isSortInDefaultState ||
    !isLocationFilterInDefaultState ||
    !isStatusFilterInDefaultState;

  const handleReset = () => {
    setName("");
    setSortOrderAndKey(defaultCandidaciesSortKeyAndOrder);
    setLocations([]);
    setStatuses([]);
  };

  return (
    <div className={clsx("flex h-full flex-col gap-4", className)}>
      <JobPipelineNumberOfCandidatesAndPublished
        job={job}
        numberOfCandidates={state.filteredAndSortedCandidacies.length}
      />
      <div className="flex items-center justify-between">
        {/* https://github.com/shadcn-ui/ui/issues/1562 */}
        <div className="relative">
          <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-slate-500" />
          <Input
            className="w-[338px] px-[36px]"
            disabled={candidaciesWithProfiles.length === 0}
            onChange={(e) => {
              setName(e.target.value);
            }}
            placeholder="Search by name"
            value={state.filterAndSortState.name}
          />
          {state.filterAndSortState.name && (
            <Button
              className="absolute right-3 top-1/2 h-auto w-auto -translate-y-1/2 transform hover:bg-transparent"
              onClick={() => {
                setName("");
              }}
              size="icon"
              variant="ghost"
            >
              <XIcon className="h-4 w-4 text-slate-500" />
            </Button>
          )}
        </div>
        <Switcher
          disabled={candidaciesWithProfiles.length === 0}
          setTab={setTab}
          tab={tab}
        />
      </div>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <JobPipelineSortBy
            disabled={candidaciesWithProfiles.length === 0}
            isInDefaultState={isSortInDefaultState}
            setSort={setSortOrderAndKey}
            sort={state.filterAndSortState.sortKeyAndOrder}
          />
          <JobPipelineLocationFilter
            allLocations={allLocations}
            disabled={candidaciesWithProfiles.length === 0}
            isInDefaultState={isLocationFilterInDefaultState}
            locations={state.filterAndSortState.locations}
            setLocations={setLocations}
          />
          <JobPipelineStatusFilter
            disabled={candidaciesWithProfiles.length === 0}
            isInDefaultState={isStatusFilterInDefaultState}
            setStatuses={setStatuses}
            statuses={state.filterAndSortState.statuses}
          />
          {shouldShowResetButton && (
            <Button
              className="rounded-full text-slate-500 hover:text-slate-500"
              onClick={handleReset}
              variant="ghost"
            >
              <RotateCcwIcon className="h-4 w-4" />
              Reset
            </Button>
          )}
        </div>
      </div>

      {tab === Tab.Table ? (
        <JobPipelineTable
          candidaciesWithProfiles={state.filteredAndSortedCandidacies}
          isEmptyState={candidaciesWithProfiles.length === 0}
          job={job}
          key={job.id} // TODO: Improve this. Force remount when job changes. Ideally we would handle this inside of the `<JobPipelineTable>`.
        />
      ) : (
        <JobPipelineKanban
          candidaciesWithProfiles={state.filteredAndSortedCandidacies}
          sort={state.filterAndSortState.sortKeyAndOrder}
        />
      )}
    </div>
  );
};
